import {
  InsomniaRiskLevelChart,
  ODI3RiskLevelChart,
} from "@/pages/common/report/WebReport2/DiseaseRiskLevelChart";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Card,
  CardContent,
  Divider,
  Link,
  List,
  ListItem,
  Stack,
  Typography,
} from "@mui/material";
import { ReactNode } from "react";
import { SectionLayout } from ".";
import InsomniaRiskDialog from "../commons/dialogs/InsomniaRiskDialog";
import SASRiskDialog from "../commons/dialogs/SASRiskDialog";
import {
  SleepSummary2InsomniaRisk,
  SleepSummary2SASRisk,
} from "../commons/sleep_summaries_2";

function getTitleSideContent(uuid: string) {
  return (
    <Link
      target="_blank"
      rel="noopener noreferrer"
      underline="none"
      sx={{
        cursor: "pointer",
        marginLeft: "auto",
        alignContent: "center",
      }}
      onClick={() => {
        window.open(`/facility/report/${uuid}/disease_risk`, "_blank");
      }}
    >
      <Stack
        direction="row"
        component="span"
        sx={{
          fontSize: "14px",
          fontWeight: "500",
          alignItems: "center",
          color: "text.secondary",
        }}
      >
        疾患リスクレポートを開く{" "}
        <OpenInNewIcon sx={{ marginLeft: "8px", fontSize: "18px" }} />
      </Stack>
    </Link>
  );
}

interface DiseaseCardProps {
  title: string;
  judgement: string;
  tooltipColor: string;
  dialog: ReactNode;
  children?: ReactNode;
  judgementWithAsterisk?: boolean;
}

function DiseaseCard({
  title,
  judgement,
  tooltipColor,
  dialog,
  children,
  judgementWithAsterisk = false,
}: DiseaseCardProps) {
  return (
    <Card variant="outlined" sx={{ width: "100%", borderRadius: "8px" }}>
      <CardContent
        sx={{
          borderBottom: "1px solid #E0E0E0",
          height: "61px",
          py: 0,
          alignContent: "center",
        }}
      >
        <Stack direction="row">
          <Stack direction="row" sx={{ alignItems: "center" }}>
            <Typography sx={{ fontSize: "16px", fontWeight: "500" }}>
              {title}
            </Typography>
            {dialog}
          </Stack>
          <Typography
            sx={{
              marginLeft: "auto",
              px: "16px",
              backgroundColor: tooltipColor,
              borderRadius: "16px",
              color: "primary.contrastText",
              fontSize: "13px",
              height: "28px",
              "& sup": {
                fontSize: "10px",
                verticalAlign: "top",
              },
            }}
          >
            {judgement}
            {judgementWithAsterisk && <sup>※</sup>}
          </Typography>
        </Stack>
      </CardContent>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

interface SASRiskCardContentProps {
  sas: SleepSummary2SASRisk;
}

function SASRiskCardContent({ sas }: SASRiskCardContentProps) {
  const ODI3Score = sas.odi3;

  return (
    <Stack>
      <Typography
        sx={{
          fontSize: "14px",
          fontWeight: "500",
          "& sup": { fontSize: "10px", verticalAlign: "14%" },
        }}
      >
        血中酸素飽和度の降下指数<sup>※</sup>
      </Typography>
      <Stack sx={{ width: "100%" }}>
        <ODI3RiskLevelChart odi3Score={ODI3Score} />
      </Stack>
      <Stack alignItems="center" sx={{ mt: "28px" }}>
        <Stack direction="row" gap={2}>
          <Typography variant="caption" fontWeight={500} fontSize="12px">
            リスクの程度：
          </Typography>
          <Typography
            variant="caption"
            sx={{
              "&::before": {
                content: "'■'",
                marginRight: 1,
                color: "#7FAACF",
              },
            }}
          >
            低い
          </Typography>
          <Typography
            variant="caption"
            sx={{
              "&::before": {
                content: "'■'",
                marginRight: 1,
                color: "#FFCE80",
              },
            }}
          >
            中
          </Typography>
          <Typography
            variant="caption"
            sx={{
              "&::before": {
                content: "'■'",
                marginRight: 1,
                color: "#FF9C97",
              },
            }}
          >
            高い
          </Typography>
          <Typography
            variant="caption"
            sx={{
              "&::before": {
                content: "'■'",
                marginRight: 1,
                color: "#FF756E",
              },
            }}
          >
            非常に高い
          </Typography>
        </Stack>
      </Stack>
      <Typography
        sx={{ color: "text.secondary", fontSize: "12px", mt: "12px" }}
      >
        ※SpO<sub>2</sub>
        が基準値から3％以上降下した1時間あたりの回数を示します。
      </Typography>
    </Stack>
  );
}

interface InsomniaRiskCardContentProps {
  insomnia: SleepSummary2InsomniaRisk;
  quantityResult: string;
  qualityResult: string;
}

function InsomniaRiskCardContent({
  insomnia,
  quantityResult,
  qualityResult,
}: InsomniaRiskCardContentProps) {
  const AISScore = insomnia.ais_score;

  const sleepResults = [
    {
      name: "睡眠の長さ",
      result: quantityResult,
    },
    {
      name: "睡眠の質",
      result: qualityResult,
    },
  ];

  const getSleepResultColor = (resultText: string) => {
    switch (resultText) {
      case "警戒":
        return "error.main";
      case "注意":
        return "#FF910B";
      default:
        return "text.primary";
    }
  };

  return (
    <Stack>
      {insomnia.result_detail === "risk_2" && (
        <Typography sx={{ color: "text.secondary", fontSize: "14px" }}>
          ※問診の結果はリスクが認められたため、リスクありと判定しています。しかし、デバイスの測定結果は睡眠の長さ・質ともに良好でした。睡眠は主観（問診）と客観がずれやすく誤認しやすいことが知られています。客観データも踏まえて心配が残る場合は医療機関を受診すると良いでしょう。
        </Typography>
      )}
      {insomnia.result_detail === "unknown" && (
        <Typography sx={{ color: "text.secondary", fontSize: "14px" }}>
          デバイスの測定結果が不足しているため判定できません。
        </Typography>
      )}
      <Typography
        sx={{
          mt: "12px",
          fontSize: "14px",
          fontWeight: "500",
          "& sup": { fontSize: "10px", verticalAlign: "14%" },
        }}
      >
        問診の結果<sup>※</sup>
      </Typography>
      <Stack sx={{ width: "100%" }}>
        <InsomniaRiskLevelChart insomniaScore={AISScore} />
      </Stack>
      <Stack alignItems="center" sx={{ mt: "28px" }}>
        <Stack direction="row" gap={2}>
          <Typography
            variant="caption"
            sx={{
              "&::before": {
                content: "'■'",
                marginRight: 1,
                color: "#7FAACF",
              },
            }}
          >
            低リスク
          </Typography>
          <Typography
            variant="caption"
            sx={{
              "&::before": {
                content: "'■'",
                marginRight: 1,
                color: "#FF9C97",
              },
            }}
          >
            疑いあり
          </Typography>
        </Stack>
      </Stack>
      <Typography
        sx={{ color: "text.secondary", fontSize: "12px", mt: "12px" }}
      >
        ※問診の根拠：アテネ不眠尺度（臨床睡眠検査マニュアル | 日本睡眠学会）
      </Typography>
      <Divider sx={{ my: 4, border: "1px dashed #0000001F" }} />
      <Typography sx={{ fontSize: "14px", fontWeight: "500" }}>
        デバイス測定による睡眠の状態
      </Typography>
      <List
        sx={{
          listStyleType: "disc",
          width: "94%",
          alignSelf: "center",
          "& li::marker": {
            fontSize: "14px",
          },
        }}
      >
        {sleepResults.map((result, idx) => (
          <ListItem key={idx} sx={{ display: "list-item", px: 0, mt: 1 }}>
            <Stack
              direction="row"
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                "& .MuiTypography-root": {
                  minWidth: "fit-content",
                },
              }}
            >
              <Typography sx={{ fontSize: "14px" }}>{result.name}</Typography>
              <Stack
                sx={{
                  height: "1px",
                  width: "100%",
                  mx: "8px",
                  color: "#0000001F",
                  borderBottom: "dotted",
                }}
              />
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: "500",
                  color: getSleepResultColor(result.result),
                }}
              >
                {result.result}
              </Typography>
            </Stack>
          </ListItem>
        ))}
      </List>
    </Stack>
  );
}

export interface DiseaseRiskJudgementSectionProps {
  uuid: string;
  sas: SleepSummary2SASRisk;
  insomnia: SleepSummary2InsomniaRisk;
  quantityResult: string;
  qualityResult: string;
}

export function DiseaseRiskJudgementSection({
  uuid,
  sas,
  insomnia,
  quantityResult,
  qualityResult,
}: DiseaseRiskJudgementSectionProps) {
  const riskDetailTooltipColors: Record<string, string> = {
    low_risk: "primary.main",
    risk_2: "#FFB10E",
    risk_1: "error.main",
    unknown: "text.secondary",
  } as const;

  return (
    <SectionLayout
      title="疾患リスクの判定詳細"
      titleSideContent={getTitleSideContent(uuid)}
    >
      <Stack direction="row" spacing={4}>
        <DiseaseCard
          title="睡眠時無呼吸症候群のリスク"
          judgement={sas.result}
          tooltipColor={riskDetailTooltipColors[sas.result_detail]}
          dialog={<SASRiskDialog />}
        >
          <SASRiskCardContent sas={sas} />
        </DiseaseCard>
        <DiseaseCard
          title="不眠症のリスク"
          judgement={insomnia.result}
          tooltipColor={riskDetailTooltipColors[insomnia.result_detail]}
          judgementWithAsterisk={insomnia.result_detail === "risk_2"}
          dialog={<InsomniaRiskDialog />}
        >
          <InsomniaRiskCardContent
            insomnia={insomnia}
            quantityResult={quantityResult}
            qualityResult={qualityResult}
          />
        </DiseaseCard>
      </Stack>
    </SectionLayout>
  );
}
